// EventListPage — event list for the selected category.
// Rendered in the right panel of EventLayout.
// Route: /t/:slug/events (index route)

import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { useSelectedEventCategory } from './EventLayout'
import {
  eventsApi,
  eventLifecycleStateLabels,
  eventLifecycleStateColors,
  type Event,
} from '@/lib/api/events'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CreateEventDialog } from './CreateEventDialog'
import {
  Plus,
  Search,
  ChevronRight,
  X,
  Link2,
  Zap,
} from 'lucide-react'
import { SortableHeader, useSortState, type SortState } from '@/components/shared/SortableHeader'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// --- Sort column type ---

type SortColumn = 'title' | 'type' | 'date' | 'status'

function sortEvents(events: Event[], sort: SortState<SortColumn>): Event[] {
  if (!sort.column) return events
  const sorted = [...events].sort((a, b) => {
    let cmp = 0
    switch (sort.column) {
      case 'title':
        cmp = a.title.localeCompare(b.title)
        break
      case 'type':
        cmp = a.event_type_name.localeCompare(b.event_type_name)
        break
      case 'date':
        cmp = (a.event_date ?? '').localeCompare(b.event_date ?? '')
        break
      case 'status': {
        const order = ['draft', 'active']
        cmp = order.indexOf(a.lifecycle_state) - order.indexOf(b.lifecycle_state)
        break
      }
    }
    return sort.direction === 'desc' ? -cmp : cmp
  })
  return sorted
}

export function EventListPage() {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const { categoryId, categoryName } = useSelectedEventCategory()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  function handleEventClick(eventId: string) {
    navigate({ to: `/t/${slug}/events/${eventId}` })
  }

  if (!categoryId) {
    return <EmptySelection />
  }

  return (
    <>
      <EventListPanel
        categoryId={categoryId}
        categoryName={categoryName}
        onEventClick={handleEventClick}
        onCreateClick={() => setCreateDialogOpen(true)}
        slug={slug}
      />

      <CreateEventDialog
        categoryId={categoryId}
        categoryName={categoryName}
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(eventId) => {
          setCreateDialogOpen(false)
          handleEventClick(eventId)
        }}
      />
    </>
  )
}

// --- Empty state when no category is selected ---

function EmptySelection() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      <div className="space-y-2 text-center">
        <ChevronRight className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm">Select a category from the tree to view its events.</p>
      </div>
    </div>
  )
}

// --- Event list panel ---

interface EventListPanelProps {
  categoryId: string
  categoryName: string
  onEventClick: (eventId: string) => void
  onCreateClick: () => void
  slug: string
}

function EventListPanel({
  categoryId,
  categoryName,
  onEventClick,
  onCreateClick,
  slug,
}: EventListPanelProps) {
  const api = useApi()
  const evtApi = eventsApi(api)

  // Filter state
  const [nameFilter, setNameFilter] = useState('')

  // Sort state
  const [sort, , handleSort] = useSortState<SortColumn>()

  // Server-side filters
  const serverFilter = {
    category_id: categoryId,
    ...(nameFilter ? { search: nameFilter } : {}),
  }

  const {
    data: events,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...queryKeys.events.all(slug), serverFilter],
    queryFn: () => evtApi.list(serverFilter),
  })

  // Client-side sorting
  const displayEvents = useMemo(() => {
    if (!events) return []
    return sortEvents(events, sort)
  }, [events, sort])

  const hasActiveFilters = nameFilter !== ''

  const clearFilters = useCallback(() => {
    setNameFilter('')
  }, [])

  return (
    <>
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4 border-b p-4">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold">{categoryName}</h1>
          <p className="text-xs text-muted-foreground">
            {events
              ? `${displayEvents.length}${displayEvents.length !== events.length ? ` of ${events.length}` : ''} event${events.length !== 1 ? 's' : ''}`
              : ''}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button size="sm" onClick={onCreateClick}>
            <Plus className="mr-1 h-4 w-4" />
            New Event
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 border-b bg-muted/10 px-4 py-2">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by title..."
            className="h-8 pl-8 text-sm"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={clearFilters}>
            <X className="mr-1 h-3 w-3" />
            Clear
          </Button>
        )}
      </div>

      {/* Column headers */}
      {events && events.length > 0 && (
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center border-b bg-muted/30 px-4 py-1.5 text-xs font-medium text-muted-foreground">
            <SortableHeader
              label="Event"
              column="title"
              sort={sort}
              onSort={handleSort}
              className="w-[30%] min-w-0"
            />
            <SortableHeader
              label="Type"
              column="type"
              sort={sort}
              onSort={handleSort}
              className="w-[12%]"
            />
            <SortableHeader
              label="Date"
              column="date"
              sort={sort}
              onSort={handleSort}
              className="w-[12%]"
            />
            <span className="w-[10%] text-center">Links</span>
            <SortableHeader
              label="Status"
              column="status"
              sort={sort}
              onSort={handleSort}
              className="w-20 justify-center"
            />
            <span className="w-20 text-right">Code</span>
          </div>
        </TooltipProvider>
      )}

      {/* Event list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <TableSkeleton rows={6} />}

        {error && (
          <div className="p-4">
            <ErrorState
              message="Failed to load events"
              technicalDetail={error.message}
              onRetry={() => refetch()}
            />
          </div>
        )}

        {events && displayEvents.length === 0 && (
          <div className="flex h-48 items-center justify-center text-muted-foreground">
            <p className="text-sm">
              {hasActiveFilters
                ? 'No events match your filters.'
                : 'No events in this category.'}
            </p>
          </div>
        )}

        {displayEvents.length > 0 && (
          <div className="divide-y">
            {displayEvents.map((evt) => (
              <EventListRow
                key={evt.id}
                event={evt}
                onClick={() => onEventClick(evt.id)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

// --- Individual event row ---

function EventListRow({
  event,
  onClick,
}: {
  event: Event
  onClick: () => void
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="flex cursor-pointer items-center px-4 py-3 transition-colors hover:bg-accent/30"
        onClick={onClick}
      >
        {/* Title + subtitle — 30% */}
        <div className="w-[30%] min-w-0">
          <div className="mb-0.5 truncate text-sm font-medium">{event.title}</div>
          <div className="truncate text-xs text-muted-foreground">
            {event.category_name} · {event.recorded_by_name}
          </div>
        </div>

        {/* Type — 12% */}
        <div className="w-[12%] pr-2">
          <Badge variant="outline" className="px-1.5 py-0 text-xs">
            {event.event_type_name}
          </Badge>
        </div>

        {/* Date — 12% */}
        <div className="w-[12%] text-xs text-muted-foreground">
          {event.event_date
            ? new Date(event.event_date + 'T00:00:00').toLocaleDateString()
            : '—'}
        </div>

        {/* Links — 10% */}
        <div className="flex w-[10%] items-center justify-center gap-2 text-xs tabular-nums text-muted-foreground">
          {event.linked_risk_count > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-0.5">
                  <Link2 className="h-3 w-3" />
                  {event.linked_risk_count}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {event.linked_risk_count} linked risk
                  {event.linked_risk_count !== 1 ? 's' : ''}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
          {event.linked_action_count > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-0.5">
                  <Zap className="h-3 w-3" />
                  {event.linked_action_count}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {event.linked_action_count} linked action
                  {event.linked_action_count !== 1 ? 's' : ''}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Status */}
        <div className="flex w-20 justify-center">
          <Badge
            variant="outline"
            className={cn(
              'px-1.5 py-0 text-xs',
              eventLifecycleStateColors[event.lifecycle_state],
            )}
          >
            {eventLifecycleStateLabels[event.lifecycle_state] ?? event.lifecycle_state}
          </Badge>
        </div>

        {/* Short code — rightmost */}
        <div className="w-20 text-right">
          {event.short_code ? (
            <span className="font-mono text-xs text-muted-foreground">{event.short_code}</span>
          ) : (
            <span className="text-xs text-muted-foreground/40">—</span>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
